import React from "react";

/**
 * Inline SVG illustrations used by Tutorial.jsx step cards.
 * All animations are CSS-only (no Lottie dependency). All artwork is purely
 * vector + CSS so it stays crisp on any DPI and themes cleanly with the brand
 * (#FF3B30 accent, dark backdrop).
 *
 * Each component is sized to a 160×160 box on desktop; the parent grid hides
 * them on viewports below `md` so mobile cards stay compact.
 */

const STYLES = `
@keyframes hg-illus-plate-pulse { 0%, 100% { opacity: 0.85; transform: scale(1); } 50% { opacity: 1; transform: scale(1.04); } }
@keyframes hg-illus-bar-grow-1 { 0%, 30%, 100% { height: 16%; } 60% { height: 38%; } }
@keyframes hg-illus-bar-grow-2 { 0%, 100% { height: 30%; } 50% { height: 56%; } }
@keyframes hg-illus-bar-grow-3 { 0%, 100% { height: 48%; } 50% { height: 78%; } }
@keyframes hg-illus-bar-grow-4 { 0%, 30%, 100% { height: 62%; } 70% { height: 96%; } }
@keyframes hg-illus-chevron-sweep { 0%, 100% { transform: translateY(0); opacity: 1; } 50% { transform: translateY(-3px); opacity: 0.85; } }
@keyframes hg-illus-sparkle { 0%, 100% { opacity: 0; transform: scale(0.6) rotate(0deg); } 50% { opacity: 1; transform: scale(1) rotate(30deg); } }
@keyframes hg-illus-trophy-glow { 0%, 100% { filter: drop-shadow(0 0 4px rgba(255,59,48,0.5)); } 50% { filter: drop-shadow(0 0 12px rgba(255,59,48,0.85)); } }
@keyframes hg-illus-bolt { 0%, 100% { opacity: 0.4; } 50% { opacity: 1; } }
`;

export function IllustrationStyles() {
    return <style>{STYLES}</style>;
}

export function BarbellIllustration() {
    return (
        <svg viewBox="0 0 200 200" width="100%" height="100%" aria-hidden>
            {/* Bar */}
            <rect x="10" y="96" width="180" height="8" rx="1" fill="#dcdcdc" />
            {/* Inner collars */}
            <rect x="56" y="86" width="6" height="28" fill="#9a9a9a" />
            <rect x="138" y="86" width="6" height="28" fill="#9a9a9a" />
            {/* Left plates — outer to inner */}
            <g style={{ animation: "hg-illus-plate-pulse 2.4s ease-in-out infinite", transformOrigin: "30px 100px" }}>
                <rect x="20" y="50" width="14" height="100" rx="2" fill="#1a1a1a" stroke="#FF3B30" strokeWidth="2" />
            </g>
            <rect x="38" y="58" width="12" height="84" rx="2" fill="#1a1a1a" stroke="#666" strokeWidth="1.5" />
            {/* Right plates */}
            <g style={{ animation: "hg-illus-plate-pulse 2.4s ease-in-out infinite 0.6s", transformOrigin: "170px 100px" }}>
                <rect x="166" y="50" width="14" height="100" rx="2" fill="#1a1a1a" stroke="#FF3B30" strokeWidth="2" />
            </g>
            <rect x="150" y="58" width="12" height="84" rx="2" fill="#1a1a1a" stroke="#666" strokeWidth="1.5" />
            {/* Bolt sparks */}
            <g style={{ animation: "hg-illus-bolt 1.6s ease-in-out infinite" }}>
                <path d="M100 30 L96 50 L102 50 L98 70" stroke="#FF3B30" strokeWidth="2" strokeLinecap="round" fill="none" />
            </g>
        </svg>
    );
}

export function StrengthChartIllustration() {
    return (
        <div className="relative h-full w-full">
            <svg viewBox="0 0 200 200" width="100%" height="100%" aria-hidden className="absolute inset-0">
                {/* Axis */}
                <line x1="20" y1="170" x2="180" y2="170" stroke="#555" strokeWidth="1" />
                <line x1="20" y1="170" x2="20" y2="30" stroke="#555" strokeWidth="1" />
                {/* Grid hints */}
                <line x1="20" y1="120" x2="180" y2="120" stroke="#2a2a2a" strokeWidth="1" strokeDasharray="2 4" />
                <line x1="20" y1="80" x2="180" y2="80" stroke="#2a2a2a" strokeWidth="1" strokeDasharray="2 4" />
                {/* Trend line */}
                <polyline
                    points="30,140 60,118 90,92 120,72 150,50 170,38"
                    fill="none"
                    stroke="#FF3B30"
                    strokeWidth="2.4"
                    strokeLinecap="round"
                />
                {/* Trend dots */}
                <g fill="#FF3B30">
                    <circle cx="30" cy="140" r="3" />
                    <circle cx="60" cy="118" r="3" />
                    <circle cx="90" cy="92" r="3" />
                    <circle cx="120" cy="72" r="3" />
                    <circle cx="150" cy="50" r="3" />
                    <circle cx="170" cy="38" r="3.5" />
                </g>
                {/* Sparkle on the head */}
                <g style={{ animation: "hg-illus-sparkle 2s ease-in-out infinite", transformOrigin: "170px 38px" }}>
                    <path d="M170 30 L172 38 L170 46 L168 38 Z" fill="#fff" />
                    <path d="M162 38 L170 36 L178 38 L170 40 Z" fill="#fff" />
                </g>
            </svg>
            {/* Animated bars behind the line */}
            <div className="absolute inset-0 flex items-end gap-1 px-5 pb-8 pt-4">
                <div className="flex-1 rounded-t bg-accent-red/15" style={{ animation: "hg-illus-bar-grow-1 3s ease-in-out infinite", height: "16%" }} />
                <div className="flex-1 rounded-t bg-accent-red/15" style={{ animation: "hg-illus-bar-grow-2 3s ease-in-out infinite 0.3s", height: "30%" }} />
                <div className="flex-1 rounded-t bg-accent-red/15" style={{ animation: "hg-illus-bar-grow-3 3s ease-in-out infinite 0.6s", height: "48%" }} />
                <div className="flex-1 rounded-t bg-accent-red/15" style={{ animation: "hg-illus-bar-grow-4 3s ease-in-out infinite 0.9s", height: "62%" }} />
            </div>
        </div>
    );
}

export function RankChevronIllustration() {
    return (
        <svg viewBox="0 0 200 200" width="100%" height="100%" aria-hidden>
            <defs>
                <linearGradient id="hg-rank-grad" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="#FF6A5A" />
                    <stop offset="100%" stopColor="#FF3B30" />
                </linearGradient>
            </defs>
            {/* Outer hex glow */}
            <polygon points="100,18 168,58 168,142 100,182 32,142 32,58"
                fill="none" stroke="#FF3B30" strokeWidth="1.5" opacity="0.4" />
            <polygon points="100,30 156,62 156,138 100,170 44,138 44,62"
                fill="#0d0d0d" stroke="url(#hg-rank-grad)" strokeWidth="2" />
            {/* Three chevrons */}
            <g style={{ animation: "hg-illus-chevron-sweep 1.8s ease-in-out infinite", transformOrigin: "100px 100px" }}>
                <polyline points="70,118 100,90 130,118" stroke="url(#hg-rank-grad)" strokeWidth="6" fill="none" strokeLinecap="round" strokeLinejoin="round" />
            </g>
            <g style={{ animation: "hg-illus-chevron-sweep 1.8s ease-in-out infinite 0.25s", transformOrigin: "100px 100px" }}>
                <polyline points="70,100 100,72 130,100" stroke="url(#hg-rank-grad)" strokeWidth="6" fill="none" strokeLinecap="round" strokeLinejoin="round" opacity="0.75" />
            </g>
            <g style={{ animation: "hg-illus-chevron-sweep 1.8s ease-in-out infinite 0.5s", transformOrigin: "100px 100px" }}>
                <polyline points="70,82 100,54 130,82" stroke="url(#hg-rank-grad)" strokeWidth="6" fill="none" strokeLinecap="round" strokeLinejoin="round" opacity="0.45" />
            </g>
        </svg>
    );
}

export function TrophyIllustration() {
    return (
        <svg viewBox="0 0 200 200" width="100%" height="100%" aria-hidden>
            <g style={{ animation: "hg-illus-trophy-glow 2.2s ease-in-out infinite", transformOrigin: "100px 100px" }}>
                {/* Cup */}
                <path d="M65 50 H135 V95 Q135 130 100 132 Q65 130 65 95 Z"
                    fill="#FF3B30" stroke="#fff" strokeWidth="2" />
                {/* Handles */}
                <path d="M65 60 Q40 60 40 80 Q40 100 65 100" fill="none" stroke="#FF3B30" strokeWidth="5" strokeLinecap="round" />
                <path d="M135 60 Q160 60 160 80 Q160 100 135 100" fill="none" stroke="#FF3B30" strokeWidth="5" strokeLinecap="round" />
                {/* Stem */}
                <rect x="92" y="132" width="16" height="22" fill="#1a1a1a" />
                {/* Base */}
                <rect x="70" y="154" width="60" height="10" rx="2" fill="#1a1a1a" stroke="#FF3B30" strokeWidth="1.5" />
            </g>
            {/* Star center */}
            <text x="100" y="98" textAnchor="middle" fontFamily="sans-serif" fontWeight="900" fontSize="38" fill="#fff">★</text>
            {/* Sparkles around */}
            <g style={{ animation: "hg-illus-sparkle 1.8s ease-in-out infinite", transformOrigin: "40px 40px" }}>
                <circle cx="40" cy="40" r="2.5" fill="#fff" />
            </g>
            <g style={{ animation: "hg-illus-sparkle 1.8s ease-in-out infinite 0.4s", transformOrigin: "165px 50px" }}>
                <circle cx="165" cy="50" r="2.5" fill="#fff" />
            </g>
            <g style={{ animation: "hg-illus-sparkle 1.8s ease-in-out infinite 0.8s", transformOrigin: "160px 130px" }}>
                <circle cx="160" cy="130" r="2.5" fill="#fff" />
            </g>
            <g style={{ animation: "hg-illus-sparkle 1.8s ease-in-out infinite 1.2s", transformOrigin: "30px 110px" }}>
                <circle cx="30" cy="110" r="2.5" fill="#fff" />
            </g>
        </svg>
    );
}
